import React from 'react';

import { useActionCta, UseActionCtaProps } from './useActionCta';

export type ButtonCtaProps = React.ComponentProps<'button'> &
  UseActionCtaProps & {
    /**
     * Render data-test-id attribute, used for cypress tests
     */
    dataTestId?: string;
    /**
     * Set the disabled html attribute. When disabled, a button
     * is not interactive and removed from the tab order. When building forms,
     * it is recommended to use the `dimmed` and `dimmedReason` properties, that
     * makes the button visually disabled but interactive.
     */
    disabled?: boolean;
    /**
     * Specifies the type of button.
     *
     * @defaultValue button
     */
    type?: 'button' | 'submit' | 'reset';
  };

/**
 * The `ButtonCta` component renders a `button` tag, and is used to
 * submit forms, or receive user input. For navigation it is recommended
 * to use the `LinkCta` component.
 *
 * @example
 * ```
 * <ButtonCta label="Shop Running" />
 * ```
 */
export const ButtonCta = React.forwardRef<HTMLButtonElement, ButtonCtaProps>(
  function ButtonCta(props, forwardedRef) {
    const {
      dataTestId,
      label,
      srOnlyLabel,
      variant,
      invert,
      size,
      color,
      mode,
      bleed,
      round,
      dimmed,
      dimmedReason,
      disabled,
      icon,
      iconEnd,
      loading,
      className,
      onClick,
      type = 'button',
      ...rest
    } = props;

    const actionCtaProps = useActionCta({
      className,
      label,
      srOnlyLabel,
      variant,
      invert,
      size,
      color,
      mode,
      round,
      bleed,
      dimmed: dimmed || disabled,
      dimmedReason,
      icon,
      iconEnd,
      loading,
    });

    const handleOnClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      if (loading) {
        e.preventDefault();
        return;
      }
      if (onClick) {
        onClick(e);
      }
    };

    return (
      <button
        ref={forwardedRef}
        data-test-id={dataTestId}
        onClick={handleOnClick}
        disabled={disabled}
        // eslint-disable-next-line react/button-has-type
        type={type}
        {...actionCtaProps}
        {...rest}
      />
    );
  }
);

// Temporarily export both ButtonCta and Button whilst we migrate to ButtonCta
export const Button = ButtonCta;
